// The interface language picker (Phase 3 localization). Renders NOTHING
// unless the VITE_I18N build flag is on, so a flag-off build is unchanged.
//
// A native <select>: keyboard, screen reader and mobile picker behavior come
// for free. Choosing a language stores it (and writes it to the signed-in
// account, best effort) through setLanguage(), then RELOADS the page, because
// text the API already returned (analyses, explanations) was rendered in the
// old language and only a refetch brings it back in the new one.
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { activeLanguage, setLanguage } from '../../i18n'
import { i18nEnabled } from '../../i18n/flag'
import { LANGUAGE_NAMES, SUPPORTED, isLang } from '../../i18n/languages'

export default function LanguageSwitcher({ className = '' }: { className?: string }) {
  if (!i18nEnabled()) return null
  return <LanguageSelect className={className} />
}

function LanguageSelect({ className }: { className: string }) {
  const { t } = useTranslation('common')
  const id = useId()
  const [value, setValue] = useState(activeLanguage)
  const [switching, setSwitching] = useState(false)

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <label htmlFor={id} className="sr-only">
        {t('language_switcher.label')}
      </label>
      <select
        id={id}
        value={value}
        disabled={switching}
        onChange={async (e) => {
          const next = e.target.value
          if (!isLang(next) || next === value) return
          setValue(next)
          setSwitching(true)
          await setLanguage(next)
          window.location.reload()
        }}
        className="bg-transparent text-xs text-gray-400 hover:text-gray-200 border border-gray-700 rounded px-1.5 py-0.5"
      >
        {SUPPORTED.map((lang) => (
          <option key={lang} value={lang} lang={lang}>
            {LANGUAGE_NAMES[lang]}
          </option>
        ))}
      </select>
    </span>
  )
}
